import { invocationDescription } from "./skillMarkdown.utils";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fullErrorText } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeftIcon, LoaderIcon } from "@/components/icons";
import { UserName } from "@/components/UserName";
import { cortexApi } from "@/api/cortex.api";
import { SkillFile, SkillFileContent, SkillStatus } from "@/proto/agentplatform/v1/skill_pb";
import { formatSkillTimestamp } from "./skill.utils";

type TabType = "overview" | "content" | "files";

function formatFrontmatterValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(v => formatFrontmatterValue(v)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const statusDot: Record<SkillStatus, string> = {
  [SkillStatus.UNAVAILABLE]: "bg-destructive",
  [SkillStatus.AVAILABLE]: "bg-emerald-500",
  [SkillStatus.UPLOADING]: "bg-amber-500",
};

const statusText: Record<SkillStatus, string> = {
  [SkillStatus.UNAVAILABLE]: "不可用",
  [SkillStatus.AVAILABLE]: "可用",
  [SkillStatus.UPLOADING]: "上传中",
};

const tabs: { id: TabType; label: string; icon: string }[] = [
  {
    id: "overview",
    label: "概览",
    icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  {
    id: "content",
    label: "SKILL.md",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
  {
    id: "files",
    label: "文件",
    icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z",
  },
];

const InfoCard = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-border/40 bg-card p-4">
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    <p className="text-sm font-medium break-all">{value || "-"}</p>
  </div>
);

interface FileTreeItem {
  name: string;
  path: string;
  isDir: boolean;
  children: FileTreeItem[];
  file?: SkillFile;
}

function buildFileTree(files: SkillFile[]): FileTreeItem[] {
  const root: FileTreeItem[] = [];
  const dirMap = new Map<string, FileTreeItem>();

  for (const f of files) {
    const parts = f.path.split("/");
    let current = root;
    let currentPath = "";

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      const isLast = i === parts.length - 1;

      if (isLast) {
        current.push({
          name: part,
          path: f.path,
          isDir: false,
          children: [],
          file: f,
        });
      } else {
        let dir = dirMap.get(currentPath);
        if (!dir) {
          dir = { name: part, path: currentPath, isDir: true, children: [] };
          dirMap.set(currentPath, dir);
          current.push(dir);
        }
        current = dir.children;
      }
    }
  }

  return root;
}

function FileNode({
  node,
  depth,
  selectedPath,
  expandedDirs,
  onToggle,
  onSelectFile,
}: {
  node: FileTreeItem;
  depth: number;
  selectedPath?: string;
  expandedDirs: Set<string>;
  onToggle: (path: string) => void;
  onSelectFile: (file: SkillFile) => void;
}) {
  const isExpanded = expandedDirs.has(node.path);
  const padLeft = depth * 16;

  if (node.isDir) {
    return (
      <div>
        <button
          onClick={() => onToggle(node.path)}
          className="w-full flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs hover:bg-muted transition-colors"
          style={{ paddingLeft: padLeft + 8 }}
        >
          <svg
            className={`h-3 w-3 flex-shrink-0 text-muted-foreground transition-transform ${isExpanded ? "rotate-90" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <svg
            className="h-3.5 w-3.5 flex-shrink-0 text-amber-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            />
          </svg>
          <span className="font-mono truncate">{node.name}</span>
        </button>
        {isExpanded &&
          node.children.map(child => (
            <FileNode
              key={child.path}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              expandedDirs={expandedDirs}
              onToggle={onToggle}
              onSelectFile={onSelectFile}
            />
          ))}
      </div>
    );
  }

  const file = node.file!;
  return (
    <button
      onClick={() => onSelectFile(file)}
      className={`w-full flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors ${
        selectedPath === node.path
          ? "bg-primary/10 border border-primary/20 -m-px"
          : "hover:bg-muted border border-transparent"
      }`}
      style={{ paddingLeft: padLeft + 8 }}
    >
      <span className="w-3 flex-shrink-0" />
      <svg
        className={`h-3.5 w-3.5 flex-shrink-0 ${file.isBinary ? "text-amber-500" : "text-blue-500"}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <span className="font-mono truncate flex-1">{node.name}</span>
    </button>
  );
}

export default function SkillsPlazaDetailPage() {
  const { uid: id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabType>("overview");
  const [selectedFile, setSelectedFile] = useState<SkillFileContent | null>(null);
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());

  const { data: skill, isLoading } = useQuery({
    queryKey: ["skill-detail", id],
    queryFn: () => cortexApi.getSkill(id, true),
    enabled: !!id,
  });

  const targetVersion = useMemo(() => {
    const versions = skill?.versions || [];
    const stable = versions.find(v => v.isStable);
    const latest = versions.find(v => v.isLatest);
    return stable || latest || versions[0];
  }, [skill]);

  const versionCode = targetVersion?.versionCode ?? 0;

  const { data: version } = useQuery({
    queryKey: ["skill-version-content", id, versionCode],
    queryFn: () => cortexApi.getSkillVersion(id, versionCode, "content"),
    enabled: !!id && versionCode > 0,
  });

  const { data: filesResp } = useQuery({
    queryKey: ["skill-version-files", id, versionCode],
    queryFn: () => cortexApi.listSkillVersionFiles(id, versionCode),
    enabled: !!id && versionCode > 0,
  });

  const files = useMemo(() => filesResp?.files ?? [], [filesResp?.files]);
  const fileTree = useMemo(() => buildFileTree(files), [files]);

  const frontmatterKV = useMemo(() => {
    if (!version?.frontmatter) return [];
    return Object.entries(version.frontmatter as Record<string, unknown>).map(([k, v]) => [
      k,
      formatFrontmatterValue(v),
    ]);
  }, [version]);

  const onExport = async () => {
    if (!id || !version) return;
    const resp = await cortexApi.exportSkillVersion(id, version.versionCode);
    const arrayBuffer = resp.data.buffer.slice(
      resp.data.byteOffset,
      resp.data.byteOffset + resp.data.byteLength,
    ) as ArrayBuffer;
    const blob = new Blob([arrayBuffer], { type: "application/zip" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${skill?.name || "skill"}-v${version.versionCode}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const forkMutation = useMutation({
    mutationFn: () =>
      cortexApi.forkSkill({
        skillId: id,
        skillVersionCode: versionCode,
      }),
    onSuccess: forked => {
      queryClient.invalidateQueries({ queryKey: ["skills-management"] });
      toast.success("Skill 克隆成功");
      navigate(`/plugins/skills/management/${forked.id}`, { replace: true });
    },
    onError: error => toast.error(fullErrorText(error)),
  });

  const onSelectFile = async (file: SkillFile) => {
    if (!id || !version) return;
    const content = await cortexApi.getSkillVersionFile(id, version.versionCode, file.path);
    setSelectedFile(content);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-5 w-96" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/plugins/skills/plaza")}
            className="h-8 w-8"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight truncate">
            {skill?.name || "Skill 详情"}
          </h1>
          {skill && (
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span className={`w-2 h-2 rounded-full ${statusDot[skill.status]}`} />
              <span className="text-muted-foreground">{statusText[skill.status]}</span>
            </span>
          )}
          {version && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium">
              {version.isStable ? "stable" : "latest"} · v{version.versionCode}
            </span>
          )}
        </div>
        {version && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => forkMutation.mutate()}
              disabled={forkMutation.isPending}
            >
              {forkMutation.isPending ? (
                <LoaderIcon className="h-3.5 w-3.5 mr-1.5" />
              ) : (
                <svg
                  className="h-3.5 w-3.5 mr-1.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
              )}
              克隆
            </Button>
            <Button size="sm" variant="outline" onClick={onExport}>
              <svg
                className="h-3.5 w-3.5 mr-1.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              导出 zip
            </Button>
          </div>
        )}
      </div>

      {skill && (
        <p className="text-sm text-muted-foreground max-w-2xl">
          仓库描述：{skill.description || "暂无仓库描述"}
        </p>
      )}
      {skill && (
        <p className="text-xs text-muted-foreground/70">
          上传者：
          <UserName userId={skill.userId} />
        </p>
      )}
      {skill && (skill.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {skill.tags.map(tag => (
            <span
              key={tag}
              className="inline-block px-2.5 py-0.5 text-[11px] rounded-md bg-secondary text-secondary-foreground/60"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {!version ? (
        <div className="rounded-2xl border border-border/60 bg-card p-16 text-center">
          <p className="text-muted-foreground text-sm">暂无版本信息</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/40 w-fit">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  tab === t.id
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d={t.icon} />
                </svg>
                {t.label}
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-6 min-h-[50vh]">
            {tab === "overview" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold">调用描述</h3>
                  <p>{invocationDescription(version?.frontmatter) || "待完善调用描述"}</p>
                  <p className="text-xs text-muted-foreground">
                    来自当前版本的 SKILL.md，Agent 根据它判断何时调用。
                  </p>
                </div>
                {frontmatterKV.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">SKILL.md 元数据</h3>
                    <div className="rounded-xl border border-border/40 overflow-hidden">
                      {frontmatterKV.map(([k, v], i) => (
                        <div
                          key={k}
                          className={`flex items-start gap-4 px-4 py-3 text-xs ${
                            i > 0 ? "border-t border-border/30" : ""
                          }`}
                        >
                          <span className="font-mono text-muted-foreground w-40 flex-shrink-0 truncate">
                            {k}
                          </span>
                          <span className="break-all flex-1">{v || "-"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold mb-3">版本信息</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <InfoCard label="版本号" value={`v${version.versionCode}`} />
                    <InfoCard label="状态" value={version.isStable ? "Stable" : "Latest"} />
                    <InfoCard label="文件数" value={`${version.fileCount ?? files.length}`} />
                    <InfoCard label="版本描述" value={version.description || ""} />
                    <InfoCard label="创建时间" value={formatSkillTimestamp(version.createdAt)} />
                    <InfoCard label="更新时间" value={formatSkillTimestamp(version.updatedAt)} />
                  </div>
                </div>
              </div>
            )}

            {tab === "content" && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <svg
                    className="h-4 w-4 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="text-sm text-muted-foreground">SKILL.md</span>
                  <span className="text-xs text-muted-foreground/50">
                    {version.content ? `${version.content.length.toLocaleString()} 字符` : "空"}
                  </span>
                </div>
                <div className="rounded-xl border border-border/40 bg-muted/30 overflow-hidden">
                  <pre className="p-5 text-xs font-mono leading-relaxed overflow-auto max-h-[60vh] whitespace-pre-wrap">
                    {version.content || "暂无内容"}
                  </pre>
                </div>
              </div>
            )}

            {tab === "files" && (
              <div className="flex gap-5">
                <div className="w-64 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-3">
                    <svg
                      className="h-4 w-4 text-muted-foreground"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      文件{" "}
                      <span className="text-muted-foreground font-normal">({files.length})</span>
                    </span>
                  </div>
                  <div className="max-h-[55vh] overflow-y-auto">
                    {fileTree.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-8 text-center">暂无文件</p>
                    ) : (
                      <div className="space-y-0.5">
                        {fileTree.map(node => (
                          <FileNode
                            key={node.path}
                            node={node}
                            depth={0}
                            selectedPath={selectedFile?.file?.path}
                            expandedDirs={expandedDirs}
                            onToggle={path =>
                              setExpandedDirs(prev => {
                                const next = new Set(prev);
                                if (next.has(path)) next.delete(path);
                                else next.add(path);
                                return next;
                              })
                            }
                            onSelectFile={onSelectFile}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  {!selectedFile ? (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
                      <svg
                        className="h-10 w-10 opacity-20"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-sm">点击左侧文件查看内容</p>
                    </div>
                  ) : selectedFile.file?.isBinary ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground rounded-xl border border-border/40 p-4">
                      <svg
                        className="h-4 w-4 text-amber-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                      二进制文件，不支持预览
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <svg
                          className="h-4 w-4 text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <span className="font-mono text-sm">{selectedFile.file?.path}</span>
                        <span className="text-xs text-muted-foreground">
                          {selectedFile.content?.length.toLocaleString()} 字符
                        </span>
                      </div>
                      <div className="rounded-xl border border-border/40 bg-muted/30 overflow-hidden">
                        <pre className="p-5 text-xs font-mono leading-relaxed overflow-auto max-h-[55vh] whitespace-pre-wrap">
                          {selectedFile.content}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
